// signalsDim.js — the `signals` dimension (alert signals over signal_tags).
//
// A first-class joined-entity dimension over SignalTags — reached PROMPT →
// SignalTags (PROMPT_HAS_SIGNAL_TAGS) exactly as `cluster` reaches
// ILO_EVENT_CLUSTER (DIM_INFO.signal + buildPromptRootAndFilter's
// `viaSignalTags`). No client scan, no in-memory verdict store.
//
// Identity is the human `command` (so the facet, the scope step, the URL, and
// the Library all agree on one id); the query layer maps command → namespace
// (signalCatalog) to filter signal_tags. Detection-only: functional extraction
// signals (skill/memory/mcp_server) sit in signal_tags but are excluded by
// construction. The listing is the tenant's ENABLED detections UNION any
// detection that fired in the current scope/range (so a just-disabled signal
// stays visible on its historical events until they TTL out).
import {
    buildPromptRootAndFilter,
    rangeToTimeRange,
    rowsToObjects,
    runQuery as runAnalyticsQuery,
    getSignalTags,
    bucketSecondsForRange,
    rangeBoundsMs,
    densifyBuckets,
} from "./analyticsClient.js";
import { shapeDimensionEntries } from "./dimAdapters.js";
import { toApiRange } from "./shell.js";
import { alertCommands, detectionNamespaces, namespaceForCommand, commandForNamespace, labelForCommand, subjectForCommand } from "./signalCatalog.js";

const NS_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" };
const ST_EVENT_ID = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID" };
const PROMPT_TS = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" };
const FACET_SPARK_BUCKETS = 24;

// Root-SignalTags field refs, for the endpoint-subject lane below.
const ST_ROOT = { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" };
const ST_SUBJECT_TYPE = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_SUBJECT_TYPE" };
const ST_SUBJECT_ID = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_SUBJECT_ID" };
const ST_TAG = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" };
const ST_EVENT_TIME = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVENT_TIME" };

// Split the registry's detection namespaces by what their tags annotate. The
// prompt-subject lane is reachable through the PROMPT→SignalTags join; the
// endpoint-subject lane is not (its tags hang off an endpoint event with no
// PROMPT row, so the INNER join drops every one), so the two need different
// queries. `subjectForCommand` is a module-load constant, not a catalog read, so
// this split is available before hydration completes.
function splitNamespacesBySubject(namespaces) {
    const prompt = [];
    const endpoint = [];
    for (const ns of namespaces) {
        const command = commandForNamespace(ns);
        if (command && subjectForCommand(command) === "endpoint") endpoint.push(ns);
        else prompt.push(ns);
    }
    return { prompt, endpoint };
}

// Scope / breakdown predicate: prompts whose signal_tags carry one of these
// signals. The dim ids are commands; map them to namespaces for the filter on
// the joined `st` step (the dispatcher's stepAlias arg is intentionally ignored —
// the namespace lives on the join, not the PROMPT root).
export function signalCombinePredicates(ids = []) {
    const ns = (ids || []).map(namespaceForCommand).filter(Boolean);
    if (!ns.length) return null;
    return { pred: { field: NS_FIELD, op: ns.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ", value: ns.length > 1 ? ns : ns[0] } };
}

// ── Facet ────────────────────────────────────────────────────────────
// Distinct detection signals + their counts AND inline sparklines, scoped +
// ranged. The detection-namespace filter bounds every GROUP BY to the registry
// (never the whole table) and excludes the functional extraction namespaces.
//
// TWO LANES, because a signal's tags can annotate a prompt or an endpoint and the
// two are not reachable the same way:
//
//   prompt-subject — `extraDims:["signal"]` forces the PROMPT→SignalTags join so
//     we can group by st.namespace. Counts DISTINCT firing prompts. The per-signal
//     total is the sum across buckets, which is exact here because each firing
//     event_id falls in exactly one bucket.
//
//   endpoint-subject — roots at SIGNAL_TAGS instead. These tags hang off an
//     endpoint config-state event that has no PROMPT row, so the join above drops
//     all of them; before this lane existed the facet read a structural 0 for
//     every endpoint-subject signal. Counts GROUPED FINDINGS (distinct tag values
//     per endpoint), which collapses repeat observations of one condition.
//     Summing across time buckets would undo that dedup, so the count and the
//     sparkline come from separate groupings — see the comments at each spec.
//
// Both lanes' totals land in one `totalByNs`, so the units differ by signal
// subject (firing prompts vs. grouped findings). That is deliberate: each is the
// count a reader of that signal's detail view will see.
//
// The listing is the ENABLED detections UNION any detection that FIRED in scope:
// entries are seeded from `alertCommands()` (so every enabled signal shows even at
// zero firings), then any namespace with firing rows is added — including a signal
// disabled AFTER it fired, so its historical events stay visible until they TTL
// out. Both lanes are filtered to ALL detection namespaces (not just enabled) so
// those disabled-but-fired rows come back — which is what makes the union work for
// an endpoint-subject signal too, since its rows now reach `totalByNs`. Ids are
// commands, matching every surface. A failed lane costs the counts, not the
// enabled baseline.
async function listEntries({ apiScope = [], offset = 0, limit = 100, search = "", sort = "activity", range, steer = null } = {}) {
    const detectionNs = detectionNamespaces();
    if (!detectionNs.length) return shapeDimensionEntries({ entries: [], total: 0 });
    const apiRange = toApiRange(range);
    const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["signal"], steer });
    if (unsupported.length) return shapeDimensionEntries({ entries: [], total: 0 });
    const { prompt: promptNs, endpoint: endpointNs } = splitNamespacesBySubject(detectionNs);
    const bucketSeconds = bucketSecondsForRange(apiRange, FACET_SPARK_BUCKETS);
    const { fromMs, toMs } = rangeBoundsMs(apiRange);
    const timeRange = rangeToTimeRange(apiRange);

    // ── Prompt-subject lane ────────────────────────────────────────────
    const promptSpec = () => {
        const nsFilter = { pred: { field: NS_FIELD, op: "OPERATOR_IN", value: promptNs } };
        const filterParts = [scopeFilter, nsFilter].filter(Boolean);
        return {
            root,
            timeRange,
            filter: filterParts.length === 1 ? filterParts[0] : { andExpr: { children: filterParts } },
            output: {
                dimensions: [
                    { field: NS_FIELD, outputAlias: "ns" },
                    { field: PROMPT_TS, outputAlias: "bucket", bucketSeconds },
                ],
                // DISTINCT firing prompts per signal per bucket — the join fans a
                // prompt out to its tags, so a plain COUNT would over-count.
                metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
            },
        };
    };

    // ── Endpoint-subject lane ──────────────────────────────────────────
    // Roots at SIGNAL_TAGS: these tags are computed from an endpoint event with
    // no PROMPT row, so the prompt lane's INNER join cannot see them (this is why
    // the facet read 0 for config_shadow). `subject_type` is pinned because
    // `subject_id` only holds an endpoint id on endpoint-subject rows.
    //
    // Two queries, deliberately: the count must come from an un-bucketed grouping.
    // Bucketing by time would let one long-lived finding count once per bucket it
    // spans, so the sparkline needs its own query.
    const endpointBase = () => ({
        root: ST_ROOT,
        timeRange,
        filter: {
            andExpr: {
                children: [
                    { pred: { field: NS_FIELD, op: "OPERATOR_IN", value: endpointNs } },
                    { pred: { field: ST_SUBJECT_TYPE, op: "OPERATOR_EQ", value: "endpoint" } },
                ],
            },
        },
    });
    // Grouped-finding count: ONE ROW PER (endpoint, finding type), so the count is
    // the row count per namespace, not a sum of metrics. Grouping by subject_id is
    // what keeps it correct across the fleet — two endpoints carrying the identical
    // finding must count twice.
    //
    // This has to agree with the number the detail lens shows, which groups by
    // (tag, layer, source_path) — the config target as a tag can see it. Two keys
    // that DON'T work: COUNT_DISTINCT(value) counts state variations rather than
    // problems (`value` embeds writer/operation, so one target re-observed by two
    // processes reads as two findings — 7 vs the real 3 on live data), and layer /
    // source_path live inside the value JSON, which this table exposes as an opaque
    // String, so they cannot be grouped on server-side at all.
    //
    // Known approximation: an endpoint carrying the SAME finding type on two
    // different config targets counts once here and twice in the detail lens. The
    // facet is a triage ranking, the lens is authoritative; a per-target count
    // needs config_target_id promoted to a signal_tags column.
    const endpointCountSpec = () => ({
        ...endpointBase(),
        output: {
            dimensions: [
                { field: NS_FIELD, outputAlias: "ns" },
                { field: ST_SUBJECT_ID, outputAlias: "subject_id" },
                { field: ST_TAG, outputAlias: "tag" },
            ],
            metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
    });
    // Observation rate for the sparkline — raw firings over time, not deduped, so
    // the shape shows when the endpoint was actually being written to.
    const endpointSeriesSpec = () => ({
        ...endpointBase(),
        output: {
            dimensions: [
                { field: NS_FIELD, outputAlias: "ns" },
                { field: ST_EVENT_TIME, outputAlias: "bucket", bucketSeconds },
            ],
            metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
    });

    // A failed lane costs its counts + sparklines, not the signals themselves —
    // the listing is registry-driven, so fall through with empty rows.
    const run = async (spec, label) => {
        try {
            return rowsToObjects(await runAnalyticsQuery(spec));
        } catch (err) {
            console.warn(`[signal] facet ${label} query failed:`, err);
            return [];
        }
    };
    const [rows, endpointCountRows, endpointSeriesRows] = await Promise.all([
        promptNs.length ? run(promptSpec(), "prompt") : Promise.resolve([]),
        endpointNs.length ? run(endpointCountSpec(), "endpoint-count") : Promise.resolve([]),
        endpointNs.length ? run(endpointSeriesSpec(), "endpoint-series") : Promise.resolve([]),
    ]);

    // Densify per namespace for the inline sparkline, and total per namespace.
    const seriesByNs = densifyBuckets(
        [...rows, ...endpointSeriesRows].map((r) => ({ id: String(r.ns ?? ""), bucket: r.bucket, value: r.value })),
        { fromMs, toMs, bucketCount: FACET_SPARK_BUCKETS, bucketSeconds },
    );
    const totalByNs = new Map();
    // Prompt lane: sum the per-bucket distinct-prompt counts. Exact, because a
    // firing event_id falls in exactly one bucket.
    for (const r of rows) {
        const ns = String(r.ns ?? "");
        totalByNs.set(ns, (totalByNs.get(ns) || 0) + (Number(r.value) || 0));
    }
    // Endpoint lane: COUNT THE ROWS. Each row is one (endpoint, finding type)
    // pair; its `value` is the observation count, which is a different and much
    // larger number — summing it here is what would put a re-fire count in the
    // sidebar where a finding count belongs.
    for (const r of endpointCountRows) {
        const ns = String(r.ns ?? "");
        totalByNs.set(ns, (totalByNs.get(ns) || 0) + 1);
    }
    const trimmed = String(search || "").trim().toLowerCase();
    // Enabled detections (baseline, so they show even at zero firings) UNION every
    // detection that fired in scope (so a since-disabled signal stays until its
    // events TTL out). A Set of commands de-dups the overlap + any slug collision.
    const enabled = new Set(alertCommands());
    const commands = new Set(enabled);
    for (const ns of totalByNs.keys()) {
        const command = commandForNamespace(ns);
        if (command) commands.add(command); // firing extraction/functional ns → no command → skip
    }
    let entries = [...commands].map((command) => {
        const ns = namespaceForCommand(command);
        return {
            id: command,
            label: labelForCommand(command),
            value: (ns && totalByNs.get(ns)) || 0,
            sparkline: (ns && seriesByNs.get(ns)) || new Array(FACET_SPARK_BUCKETS).fill(0),
            // Mark the disabled-but-fired rows so the enabled-first grouping reads
            // as deliberate, not a broken sort. This is a short status BADGE (an
            // inline tag on the row title) — distinct from `note`, the two-row
            // secondary-line slot the sidebar runtime still supports and we leave
            // available for a future dim. Enabled rows carry no badge.
            badge: enabled.has(command) ? "" : "disabled",
        };
    });
    if (trimmed) entries = entries.filter((e) => e.label.toLowerCase().includes(trimmed) || e.id.toLowerCase().includes(trimmed));
    // Enabled signals always group ABOVE disabled-but-fired ones — otherwise a
    // turned-off signal with historical firings would sort over a quiet enabled
    // one on activity. Sort mode (Most fired / A–Z) then orders within each group.
    const within = sort === "name"
        ? (a, b) => String(a.label).localeCompare(String(b.label))
        : (a, b) => (b.value - a.value) || String(a.label).localeCompare(String(b.label));
    const sorted = entries.sort(
        (a, b) => (Number(enabled.has(b.id)) - Number(enabled.has(a.id))) || within(a, b),
    );
    return shapeDimensionEntries({ entries: sorted.slice(offset, offset + limit), total: sorted.length });
}

async function count(opts = {}) {
    const { entries = [] } = await listEntries({ ...opts, offset: 0, limit: 1000 });
    return entries.length;
}

// Per-page badge query: which DETECTION signals each of these prompts fired, for
// the trace-list badges. Reads signal_tags for the visible event_ids, maps each
// namespace → its command (dropping functional namespaces + facet rows that
// have no command), and collapses to one badge per signal. Scoped to all
// detection namespaces (not just enabled) so a prompt that fired a since-disabled
// signal still shows that badge — a badge means "fired here", which stays true
// regardless of current deploy state. Returns
// Map<event_id, fired[]> in the same shape the trace renderers consumed from the
// old scan store ({ command, type, value }). The window is the rendered page —
// scalable, no scan.
export async function fetchSignalBadges(eventIds, range = null) {
    const ids = Array.isArray(eventIds) ? eventIds.filter(Boolean) : [];
    if (!ids.length) return new Map();
    let rows = [];
    try {
        // Normalize the range (callers pass a preset id like "1M" or a
        // {from,to}); getSignalTags wants {from,to} for partition pruning. The
        // event_id IN filter is what actually scopes, so range is a perf hint only.
        // Restrict to detection namespaces server-side so the badge pass skips the
        // heavy functional `value` blobs (e.g. mcp_server tool-surface JSON).
        rows = await getSignalTags(ids, toApiRange(range), { namespaces: detectionNamespaces() });
    } catch (err) {
        console.warn("[signal] badge query failed:", err);
        return new Map();
    }
    const byEvent = new Map();
    for (const r of rows) {
        if (!r || !r.event_id) continue;
        const command = commandForNamespace(String(r.namespace ?? ""));
        if (!command) continue; // functional namespace / facet row → not an alert badge
        let byCmd = byEvent.get(r.event_id);
        if (!byCmd) { byCmd = new Map(); byEvent.set(r.event_id, byCmd); }
        if (!byCmd.has(command)) {
            byCmd.set(command, {
                command,
                type: `${r.namespace}.${r.tag}`,
                value: { match: r.value != null ? String(r.value) : null },
            });
        }
    }
    const out = new Map();
    for (const [id, byCmd] of byEvent) out.set(id, [...byCmd.values()]);
    return out;
}

// ── Adapter export ─────────────────────────────────────────────────
export const signalAdapter = {
    id:     "signal",
    plural: "signals",
    label:  "Signals",
    description: "Security signals the backend computed over each prompt (signal_tags)",

    predicate: signalCombinePredicates,
    // The predicate targets the joined `st` step, so the join must be present
    // whenever the predicate runs (cross-dim breakdowns). A signal SCOPE step
    // flows into the API scope (no rewriteScopeStep) — getTraces and
    // buildPromptRootAndFilter both add the PROMPT → SignalTags join + namespace
    // predicate, so scoping to a signal works exactly like scoping to a cluster.
    predicateExtraDims: ["signal"],
    // The predicate is "st.namespace is one of the ids' namespaces", so
    // grouping by the namespace gives every signal's flow links in one read.
    flowGroup: { field: NS_FIELD, idsForValue: (value, ids) => ids.filter((id) => namespaceForCommand(id) === value) },
    listEntries,
    count,
    labelFor: labelForCommand,

    composesWithPromptGraph: true,
    bodyFitsInListProjection: true,
    countBatchable: false,
};

// ── Transitional shims (removed once the lenses stop scanning) ───────
// The pre-cutover client scan exposed these to scope child facets to a signal's
// fired prompts and to refetch when the scan settled. With `signals` a real
// scoped dim, scoping rides the viaSignalTags join, so the fired-id filter is a
// no-op and the refetch token is static. Kept (inert) so apiClient / ScopeSidebar
// imports hold until the scan engine is deleted.
export function signalScopeFilter() { return null; }
export function signalResultsToken() { return "signals:server"; }
